import type { Capacidade, Membro, Papel, Projeto, StatusWorker, Usuario } from "@escalando/contracts";
import { useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { Carregando, dataHora, useDados } from "../comum";

type Energia = { pedido: string; drenar: boolean; manter_ligado_ate?: string; ultima_acao?: { tipo: string; motivo: string; em: string; mercado?: string } };

/** Worker: status e botão de energia (só o Levy, D15). */
export function Worker() {
  const st = useDados(() => api<{ worker?: StatusWorker; energia?: Energia }>("GET", "worker"));
  const [msg, setMsg] = useState<string>();
  const pedir = async (acao: string, horas?: number) => {
    setMsg(undefined);
    try {
      await api("POST", "worker/energia", { acao, ...(horas ? { ate: new Date(Date.now() + horas * 3600_000).toISOString() } : {}) });
      setMsg("Pedido registrado; o controle aplica em até 1 minuto.");
      st.recarregar();
    } catch (x) {
      setMsg(x instanceof ErroApi ? x.message : "falhou");
    }
  };
  if (!st.dados) return <Carregando erro={st.erro} />;
  const { worker: w, energia: e } = st.dados;
  const vivo = w && Date.now() - new Date(w.enviado_em).getTime() < 120_000;
  return (
    <>
      <h1>Worker</h1>
      <div className="cartao">
        <strong>{vivo ? `Ligado · ${w.estado}` : "Desligado"}</strong>
        {w && <div className="suave">{w.instance_type} · {w.mercado} · último sinal {dataHora(w.enviado_em)}</div>}
        {vivo && w.task_id && <div>Trabalhando em <a href={`#/tarefa/${w.task_id}`}>{w.task_id}</a>{w.etapa ? ` (${w.etapa})` : ""}</div>}
        {vivo && w.sessao_ssm_ativa && <div className="suave">Modo construção (alguém conectado)</div>}
        {e?.manter_ligado_ate && <div className="suave">Manter ligado até {dataHora(e.manter_ligado_ate)}</div>}
        {e?.ultima_acao && <div className="suave">Última ação: {e.ultima_acao.tipo} em {dataHora(e.ultima_acao.em)} — {e.ultima_acao.motivo}</div>}
      </div>
      <div className="linha">
        <button className="primario" onClick={() => void pedir("ligar")}>Ligar</button>
        <button className="perigo" onClick={() => void pedir("desligar")}>Desligar</button>
        <button onClick={() => void pedir("manter_ligado", 2)}>Manter ligado 2 h</button>
      </div>
      {msg && <p className="suave">{msg}</p>}
    </>
  );
}

type Pessoa = Usuario & { projetos: Membro[] };
const CAPACIDADES: [Capacidade, string][] = [["publicar", "Publicar"], ["enviar", "Enviar e-mail/mensagem"], ["aprovar_producao", "Aprovar produção"], ["ver_custos", "Ver custos"], ["escolher_modelo", "Escolher modelo"]];

/** Pessoas e projetos (só o Levy; A13: convites e desativação são ação humana dele). */
export function Pessoas() {
  const ps = useDados(() => api<{ pessoas: Pessoa[] }>("GET", "pessoas"));
  const pj = useDados(() => api<{ projetos: Projeto[] }>("GET", "projetos"));
  const [msg, setMsg] = useState<{ ok?: string; erro?: string }>({});
  const [form, setForm] = useState({ user_id: "", nome: "", email: "", projeto: "kids", papel: "colaborador" as Papel, caps: [] as Capacidade[] });

  const agir = async (f: () => Promise<unknown>, ok: string) => {
    setMsg({});
    try {
      await f();
      setMsg({ ok });
      ps.recarregar();
    } catch (x) {
      setMsg({ erro: x instanceof ErroApi ? x.message : "falhou" });
    }
  };
  const convidar = (e: FormEvent) => {
    e.preventDefault();
    void agir(() => api("POST", "pessoas", {
      user_id: form.user_id, nome: form.nome, email: form.email,
      projetos: [{ project_id: form.projeto, papel: form.papel, capacidades: form.caps }],
    }), `Convite enviado para ${form.email}.`);
  };
  const nomeProjeto = (id: string) => pj.dados?.projetos.find((p) => p.project_id === id)?.nome ?? id;

  return (
    <>
      <h1>Pessoas</h1>
      {msg.ok && <p className="ok">{msg.ok}</p>}
      {msg.erro && <p className="erro">{msg.erro}</p>}
      {!ps.dados ? <Carregando erro={ps.erro} /> : ps.dados.pessoas.map((p) => (
        <div key={p.user_id} className="cartao">
          <div className="linha" style={{ justifyContent: "space-between" }}>
            <strong>{p.nome}</strong>
            <span className="suave">{p.admin_global ? "administrador" : p.ativo ? "ativo" : "desativado"}</span>
          </div>
          <div className="suave">{p.email}</div>
          <div className="suave">{p.projetos.map((m) => `${nomeProjeto(m.project_id)} (${m.papel}${m.capacidades.length ? `: ${m.capacidades.join(", ")}` : ""})`).join(" · ") || (p.admin_global ? "todos os projetos" : "sem projetos")}</div>
          {!p.admin_global && (
            <button style={{ marginTop: 8 }} className={p.ativo ? "perigo" : ""} onClick={() => void agir(() => api("POST", `pessoas/${p.user_id}/${p.ativo ? "desativar" : "reativar"}`, {}), p.ativo ? "Acesso desativado." : "Acesso reativado.")}>
              {p.ativo ? "Desativar acesso" : "Reativar acesso"}
            </button>
          )}
        </div>
      ))}
      <h2>Convidar pessoa</h2>
      <form className="cartao" onSubmit={convidar}>
        <label htmlFor="n">Nome</label>
        <input id="n" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        <label htmlFor="u">Identificador curto (ex.: erica)</label>
        <input id="u" required pattern="[a-z][a-z0-9\-]{1,30}" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} />
        <label htmlFor="m">E-mail</label>
        <input id="m" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <label htmlFor="pj">Projeto</label>
        <select id="pj" value={form.projeto} onChange={(e) => setForm({ ...form, projeto: e.target.value })}>
          {pj.dados?.projetos.map((p) => <option key={p.project_id} value={p.project_id}>{p.nome}</option>)}
        </select>
        <label htmlFor="pa">Papel</label>
        <select id="pa" value={form.papel} onChange={(e) => setForm({ ...form, papel: e.target.value as Papel })}>
          <option value="colaborador">Colaborador</option><option value="visualizador">Visualizador</option><option value="admin">Administrador do projeto</option>
        </select>
        <label>Permissões extras</label>
        {CAPACIDADES.map(([c, n]) => (
          <label key={c} className="linha" style={{ margin: 0 }}>
            <input type="checkbox" style={{ width: "auto", minHeight: 0 }} checked={form.caps.includes(c)}
              onChange={(e) => setForm({ ...form, caps: e.target.checked ? [...form.caps, c] : form.caps.filter((x) => x !== c) })} /> {n}
          </label>
        ))}
        <p><button className="primario" type="submit">Enviar convite</button></p>
      </form>
    </>
  );
}
